import React, { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { setLanguage } from '@locales/helpers';
import { yupValidationService } from '@utils/library';
import { DateTimeService } from '@utils/library/dateTimeService';
import { reactRouterService } from '@utils/library/reactRouter.service';
import { waitAntdStylesLoad } from '@utils/waitAntdLoad';
import Preloader from 'components/shared/preloader';
import { ENUM_LOCALE_CODE } from 'global.enums';
import { observer } from 'mobx-react';
import { useMobxStore } from 'store/mobx';
import { AppContext, IAppContext } from './context';
import { useDocumentDirection } from './hooks/useDocumentDirection';

interface IProps {
  registration: boolean;
  children: ReactNode;
  useClientTranslation: boolean;
}

const ContextGuard = ({ registration, children, useClientTranslation }: IProps) => {
  const { i18n } = useTranslation();
  const history = useHistory();
  const { accountStore, appSettingStore, featuresStore, clientSideTranslationStore } = useMobxStore();

  const { isFeaturesProcessing, fetchFeatureFlags, isFeaturesLoaded } = featuresStore;

  const { clientSideTranslationsLoading, fetchTranslations } = clientSideTranslationStore;

  const { setI18next } = appSettingStore;

  const [trackersInitialized, setTrackersInitialized] = useState(false);
  const [antdStylesLoaded, setAntdStylesLoaded] = useState(false);

  const language = i18n.language as ENUM_LOCALE_CODE;

  const {
    generalSettingsLoading,
    userInfoLoading,
    initialUserInfo,
    utcOffsetInHours,
    userCountryInfo,
    fetchUserIpInfo,
  } = accountStore;

  const documentDirection = useDocumentDirection(language);

  useEffect(() => {
    setI18next(i18n);
  }, [i18n, language, setI18next]);

  useEffect(() => {
    reactRouterService.initHistory(history);
  }, [history]);

  useEffect(() => {
    DateTimeService.setOffset(utcOffsetInHours);
  }, [utcOffsetInHours]);

  useEffect(() => {
    if (useClientTranslation) {
      void fetchTranslations(language);
    }
  }, [fetchTranslations, language, useClientTranslation]);

  useEffect(() => {
    void accountStore.fetchGeneralSettings();

    if (!initialUserInfo) {
      void accountStore.fetchUserInfo({ showLoader: true, isInitialRequest: true });
    }

    void loadAntdStyles();

    yupValidationService.initializeValidationMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (initialUserInfo && !isFeaturesLoaded) {
      void fetchFeatureFlags();
    }
  }, [fetchFeatureFlags, initialUserInfo, isFeaturesLoaded]);

  useEffect(() => {
    if (trackersInitialized || !initialUserInfo) {
      return;
    }

    if (registration || initialUserInfo?.id) {
      setTrackersInitialized(true);
    }
  }, [registration, trackersInitialized, initialUserInfo]);

  useEffect(() => {
    if (userCountryInfo) {
      return;
    }
    void fetchUserIpInfo();
  }, [fetchUserIpInfo, userCountryInfo]);

  const loadAntdStyles = async () => {
    try {
      await waitAntdStylesLoad();
      setAntdStylesLoaded(true);
    } catch (err) {
      console.error('Failed to load antd styles');
    }
  };

  const props: IAppContext = { language, setLanguage, documentDirection, i18next: i18n };

  if (
    generalSettingsLoading ||
    userInfoLoading ||
    isFeaturesProcessing ||
    !antdStylesLoaded ||
    clientSideTranslationsLoading
  ) {
    return <Preloader />;
  }

  return <AppContext.Provider value={props}>{children}</AppContext.Provider>;
};

export default observer(ContextGuard);
